import Matter from 'matter-js';
import { BOUNDARY_DISTANCE } from '@/canvas/consts/layout';
import type { IPhysicsEngine } from '@/canvas/types/Block';

/**
 * 物理エンジン（Matter.js）の統合クラス
 */
export class PhysicsEngine implements IPhysicsEngine {
  private engine: Matter.Engine;
  private world: Matter.World;
  private boxBodies: {
    left: Matter.Body;
    right: Matter.Body;
    bottom: Matter.Body;
  } | null = null;

  private boundaryBoxes: {
    top: Matter.Body;
    bottom: Matter.Body;
    left: Matter.Body;
    right: Matter.Body;
  } | null = null;

  /** アキュムレータ（単位: ms） */
  private accumulator = 0;
  /**
   * 物理ステップの刻み幅（実時間 ms）。
   * 1000/60 ≈ 16.67ms = 60fps 相当。
   */
  private readonly STEP_INTERVAL_MS = 1000 / 60;
  /**
   * Matter.Engine.update に渡す固定デルタ。
   * 値 1 = 60fps 時の Pixi ticker.deltaTime と同等で、
   * 既存の gravity.scale チューニングをそのまま維持できる。
   */
  private readonly MATTER_DELTA = 1;
  /**
   * 1 フレームあたりの最大サブステップ数（スパイラル・オブ・デス防止）。
   */
  private readonly MAX_SUBSTEPS = 5;

  constructor() {
    // Matter.jsエンジンを作成
    this.engine = Matter.Engine.create();
    this.world = this.engine.world;

    // 重力を設定（デフォルトは下向き）
    // scaleを大きくすることで落下速度を上げる
    this.engine.world.gravity.y = 1;
    this.engine.world.gravity.scale = 0.5; // 0.001から0.005に変更（5倍速）

    // ソルバ反復数を増やしてすり抜けを抑制（既定は各6）
    this.engine.positionIterations = 8;
    this.engine.velocityIterations = 8;
  }

  /**
   * コの字型箱の物理ボディを作成
   * @param x 箱の左端X座標
   * @param y 箱の上端Y座標
   * @param width 箱の幅
   * @param height 箱の高さ
   * @param wallThickness 壁の厚さ
   */
  createBox(
    x: number,
    y: number,
    width: number,
    height: number,
    wallThickness: number = 20,
  ): void {
    // 既存の箱を削除
    if (this.boxBodies) {
      Matter.World.remove(this.world, [
        this.boxBodies.left,
        this.boxBodies.right,
        this.boxBodies.bottom,
      ]);
    }

    // 左壁
    const leftWall = Matter.Bodies.rectangle(
      x - wallThickness / 2,
      y + height / 2,
      wallThickness,
      height,
      { isStatic: true },
    );

    // 右壁
    const rightWall = Matter.Bodies.rectangle(
      x + width + wallThickness / 2,
      y + height / 2,
      wallThickness,
      height,
      { isStatic: true },
    );

    // 底（上面を y + height - wallThickness にし、左右の壁下端と揃える）
    const bottom = Matter.Bodies.rectangle(
      x + width / 2,
      y + height - wallThickness / 2,
      width,
      wallThickness,
      { isStatic: true },
    );

    this.boxBodies = {
      left: leftWall,
      right: rightWall,
      bottom: bottom,
    };

    Matter.World.add(this.world, [leftWall, rightWall, bottom]);
  }

  /**
   * 物理エンジンを更新（アキュムレータ方式 / 固定タイムステップ）
   * @param elapsedMs 前フレームからの実経過時間（ミリ秒）
   *
   * レンダリング fps と物理ステップを分離することで、低電力モード等で
   * fps が落ちた場合も 1 ステップあたりの積分量を一定に保ち、
   * 壁のすり抜けを防ぐ。
   */
  update(elapsedMs: number): void {
    // バックグラウンド復帰等の極端なフレームをクランプ
    const clamped = Math.min(
      elapsedMs,
      this.STEP_INTERVAL_MS * this.MAX_SUBSTEPS,
    );
    this.accumulator += clamped;

    let steps = 0;
    while (
      this.accumulator >= this.STEP_INTERVAL_MS &&
      steps < this.MAX_SUBSTEPS
    ) {
      Matter.Engine.update(this.engine, this.MATTER_DELTA);
      this.accumulator -= this.STEP_INTERVAL_MS;
      steps++;
    }

    // MAX_SUBSTEPS に達した場合は溜まりすぎをリセットしてスパイラルを防止
    if (steps === this.MAX_SUBSTEPS) {
      this.accumulator = 0;
    }
  }

  /**
   * Matter.jsエンジンを取得
   */
  getEngine(): Matter.Engine {
    return this.engine;
  }

  /**
   * Matter.jsワールドを取得
   */
  getWorld(): Matter.World {
    return this.world;
  }

  /**
   * 境界箱の物理ボディを作成（canvas外の四方に配置）
   * @param screenWidth 画面幅
   * @param screenHeight 画面高さ
   * @param boundaryThickness 境界箱の厚さ（デフォルト: 50）
   */
  createBoundaryBoxes(
    screenWidth: number,
    screenHeight: number,
    boundaryThickness: number = 50,
  ): void {
    // 既存の境界箱を削除
    if (this.boundaryBoxes) {
      Matter.World.remove(this.world, [
        this.boundaryBoxes.top,
        this.boundaryBoxes.bottom,
        this.boundaryBoxes.left,
        this.boundaryBoxes.right,
      ]);
    }

    // canvas 外の四方にセンサー境界ボックスを生成する設定配列
    // [x, y, width, height, label]
    const d = BOUNDARY_DISTANCE;
    const t = boundaryThickness;
    const w = screenWidth;
    const h = screenHeight;
    type BoundaryKey = 'top' | 'bottom' | 'left' | 'right';
    const configs: Array<[number, number, number, number, BoundaryKey]> = [
      [w / 2, -d - t / 2, w * 3, t, 'top'],
      [w / 2, h + d + t / 2, w * 3, t, 'bottom'],
      [-d - t / 2, h / 2, t, h * 3, 'left'],
      [w + d + t / 2, h / 2, t, h * 3, 'right'],
    ];

    const bodies = configs.map(([x, y, bw, bh, key]) =>
      Matter.Bodies.rectangle(x, y, bw, bh, {
        isStatic: true,
        isSensor: true,
        label: `boundary${key.charAt(0).toUpperCase()}${key.slice(1)}`,
      }),
    ) as [Matter.Body, Matter.Body, Matter.Body, Matter.Body];

    const [top, bottom, left, right] = bodies;

    this.boundaryBoxes = { top, bottom, left, right };
    Matter.World.add(this.world, bodies);
  }

  /**
   * 境界箱のボディを取得（ゲーム終了判定用）
   */
  getBoundaryBoxes(): {
    top: Matter.Body;
    bottom: Matter.Body;
    left: Matter.Body;
    right: Matter.Body;
  } | null {
    return this.boundaryBoxes;
  }

  /**
   * 物理ボディをワールドに追加
   */
  addBody(body: Matter.Body): void {
    Matter.World.add(this.world, body);
  }

  /**
   * 物理ボディをワールドから削除
   */
  removeBody(body: Matter.Body): void {
    Matter.World.remove(this.world, body);
  }

  /**
   * 接触イベントのリスナーを設定
   */
  onCollision(
    callback: (event: Matter.IEventCollision<Matter.Engine>) => void,
  ): void {
    Matter.Events.on(this.engine, 'collisionStart', callback);
  }

  /**
   * 接触イベントのリスナーを削除
   */
  offCollision(
    callback: (event: Matter.IEventCollision<Matter.Engine>) => void,
  ): void {
    Matter.Events.off(this.engine, 'collisionStart', callback);
  }

  /**
   * 破棄処理
   */
  destroy(): void {
    if (this.boxBodies) {
      Matter.World.remove(this.world, [
        this.boxBodies.left,
        this.boxBodies.right,
        this.boxBodies.bottom,
      ]);
    }
    if (this.boundaryBoxes) {
      Matter.World.remove(this.world, [
        this.boundaryBoxes.top,
        this.boundaryBoxes.bottom,
        this.boundaryBoxes.left,
        this.boundaryBoxes.right,
      ]);
    }
    Matter.Engine.clear(this.engine);
  }
}
